import React, { useEffect, useState } from 'react';
import {
  Box,
  Drawer,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Typography,
  Avatar,
  Divider,
  useMediaQuery,
  useTheme,
  Skeleton,
} from '@mui/material';
import {
  Dashboard as DashboardIcon,
  AccountBalanceWallet as PortfolioIcon,
  Assessment as InsightsIcon,
  Settings as SettingsIcon,
  Person as PersonIcon,
  Build as BuildIcon,
} from '@mui/icons-material';
import { useNavigate, useLocation } from 'react-router-dom';
import { androidColors } from '../../theme/androidTheme';
import { authService, User } from '../../services/authService';
import { useTranslation } from 'react-i18next';

interface SidebarProps {
  open: boolean;
  onClose: () => void;
}

const Sidebar: React.FC<SidebarProps> = ({ open, onClose }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const [user, setUser] = useState<User | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const { t } = useTranslation();
  
  useEffect(() => {
    const fetchUserData = async () => {
      setIsLoading(true);
      try {
        // Toujours essayer de récupérer les données fraîches de l'API
        const userInfo = await authService.getUserInfo();
        if (userInfo) {
          const newUser: User = {
            id: userInfo.id || '',
            firstName: userInfo.firstname || '',
            lastName: userInfo.lastname || '',
            email: userInfo.email || '',
            premium: userInfo.premium || 'no',
            onboard: userInfo.onboard || 'no',
            createdAt: userInfo.createdAt || new Date().toISOString(),
          };
          setUser(newUser);
        } else {
          // Si l'API échoue, utiliser les données du service
          const currentUser = authService.getUser();
          if (currentUser) {
            setUser(currentUser);
          }
        }
      } catch (error) {
        console.error('Erreur lors de la récupération des données utilisateur:', error);
        // En cas d'erreur, utiliser les données du service
        const currentUser = authService.getUser();
        if (currentUser) {
          setUser(currentUser);
        }
      } finally {
        setIsLoading(false);
      }
    };

    fetchUserData();
    // Mettre à jour les données toutes les 5 minutes
    const intervalId = setInterval(fetchUserData, 5 * 60 * 1000);

    return () => {
      clearInterval(intervalId);
    };
  }, []);

  const userName = user?.firstName ? `${user.firstName} ${user.lastName || ''}`.trim() : t('common.user');
  const userInitials = userName.split(' ').map(n => n[0]).join('').toUpperCase();
  const userPlan = user?.premium === 'yes' ? t('common.premium') : t('common.lifetime');

  const menuItems = [
    {
      text: t('nav.dashboard'),
      icon: <DashboardIcon />,
      path: '/dashboard',
    },
    {
      text: t('nav.portfolio'),
      icon: <PortfolioIcon />,
      path: '/portfolio',
    },
    {
      text: t('nav.insights'),
      icon: <InsightsIcon />,
      path: '/insights',
    },
    {
      text: t('nav.tools'),
      icon: <BuildIcon />,
      path: '/tools',
    },
    {
      text: t('nav.settings'),
      icon: <SettingsIcon />,
      path: '/settings',
    },
  ];

  const handleNavigation = (path: string) => {
    navigate(path);
    if (isMobile) {
      onClose();
    }
  };

  const drawerWidth = 280;

  const drawerContent = (
    <Box
      sx={{
        width: drawerWidth,
        height: '100%',
        backgroundColor: androidColors.cardBackground,
        borderRight: `1px solid ${androidColors.borderColor}`,
        overflowX: 'hidden',
        overflowY: 'auto',
        display: 'flex',
        flexDirection: 'column',
      }}
    >
      {/* Header avec informations utilisateur */}
      <Box
        sx={{
          p: 3,
          borderBottom: `1px solid ${androidColors.borderColor}`,
        }}
      >
        {/* Informations utilisateur */}
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          {isLoading ? (
            <>
              <Skeleton
                variant="circular"
                width={48}
                height={48}
                sx={{ bgcolor: `${androidColors.primaryGold}20` }}
              />
              <Box sx={{ flex: 1, minWidth: 0 }}>
                <Skeleton
                  variant="text"
                  width="60%"
                  height={24}
                  sx={{ bgcolor: `${androidColors.primaryGold}20` }}
                />
                <Skeleton
                  variant="text"
                  width="40%"
                  height={20}
                  sx={{ bgcolor: `${androidColors.primaryGold}20` }}
                />
              </Box>
            </>
          ) : (
            <>
          <Avatar
            sx={{
              width: 48,
              height: 48,
              backgroundColor: androidColors.primaryGold,
              color: androidColors.darkBackground,
              fontSize: '18px',
              fontWeight: 'bold',
            }}
          >
            {userInitials}
          </Avatar>
          <Box sx={{ flex: 1, minWidth: 0, overflow: 'hidden' }}>
            <Typography
              variant="subtitle1"
              sx={{
                color: androidColors.textColor,
                fontWeight: 'bold',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
              }}
            >
              {userName}
            </Typography>
            <Typography
              variant="body2"
              sx={{
                color: androidColors.textSecondary,
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
              }}
            >
              {userPlan}
            </Typography>
          </Box>
            </>
          )}
        </Box>
      </Box>

      {/* Menu de navigation */}
      <List sx={{ px: 2, py: 2, flex: 1 }}>
        {menuItems.map((item) => {
          const isActive = location.pathname === item.path;
          
          return (
            <ListItem key={item.text} disablePadding sx={{ mb: 1 }}>
              <ListItemButton
                onClick={() => handleNavigation(item.path)}
                sx={{
                  borderRadius: '12px',
                  backgroundColor: isActive ? `${androidColors.primaryGold}20` : 'transparent',
                  border: isActive ? `1px solid ${androidColors.primaryGold}` : '1px solid transparent',
                  '&:hover': {
                    backgroundColor: `${androidColors.primaryGold}10`,
                  },
                  py: 1.5,
                }}
              >
                <ListItemIcon
                  sx={{
                    color: isActive ? androidColors.primaryGold : androidColors.textSecondary,
                    minWidth: 40,
                  }}
                >
                  {item.icon}
                </ListItemIcon>
                <ListItemText
                  primary={item.text}
                  sx={{
                    '& .MuiListItemText-primary': {
                      color: isActive ? androidColors.primaryGold : androidColors.textColor,
                      fontWeight: isActive ? 'bold' : 'normal',
                    },
                  }}
                />
              </ListItemButton>
            </ListItem>
          );
        })}
      </List>

      <Divider sx={{ borderColor: androidColors.borderColor, mx: 2 }} />

      {/* Section profil */}
      <List sx={{ px: 2, py: 2 }}>
        <ListItem disablePadding>
          <ListItemButton
            onClick={() => handleNavigation('/profile')}
            sx={{
              borderRadius: '12px',
              backgroundColor: location.pathname === '/profile' ? `${androidColors.primaryGold}20` : 'transparent',
              border: location.pathname === '/profile' ? `1px solid ${androidColors.primaryGold}` : '1px solid transparent',
              '&:hover': {
                backgroundColor: `${androidColors.primaryGold}10`,
              },
              py: 1.5,
            }}
          >
            <ListItemIcon
              sx={{
                color: location.pathname === '/profile' ? androidColors.primaryGold : androidColors.textSecondary,
                minWidth: 40,
              }}
            >
              <PersonIcon />
            </ListItemIcon>
            <ListItemText
              primary={t('nav.profile')}
              sx={{
                '& .MuiListItemText-primary': {
                  color: location.pathname === '/profile' ? androidColors.primaryGold : androidColors.textColor,
                  fontWeight: location.pathname === '/profile' ? 'bold' : 'normal',
                },
              }}
            />
          </ListItemButton>
        </ListItem>
      </List>

      {/* Logo en bas */}
      <Box
        sx={{
          p: 2,
          borderTop: `1px solid ${androidColors.borderColor}`,
          display: 'flex',
          alignItems: 'center',
          gap: 1,
          justifyContent: 'space-between',
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <img
            src="/logo-wb.png"
            alt="CryptoNVX"
            style={{ height: 32 }}
          />
          <Typography
            variant="caption"
            sx={{
              color: androidColors.textSecondary,
              fontSize: '0.75rem',
            }}
          >
            {t('app.version')}
          </Typography>
        </Box>
      </Box>
    </Box>
  );

  return (
    <Drawer
      variant={isMobile ? 'temporary' : 'persistent'}
      open={open}
      onClose={onClose}
      sx={{
        width: open ? drawerWidth : 0,
        flexShrink: 0,
        '& .MuiDrawer-paper': {
          width: drawerWidth,
          boxSizing: 'border-box',
          backgroundColor: androidColors.cardBackground,
          borderRight: `1px solid ${androidColors.borderColor}`,
          overflowX: 'hidden',
          overflowY: 'auto',
        },
      }}
      ModalProps={{
        keepMounted: true, // Better open performance on mobile.
      }}
    >
      {drawerContent}
    </Drawer>
  );
};

export default Sidebar;